import { Form, Formik } from "formik";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DetailField } from "@/core/components/detail-field";
import { FormInput } from "@/core/components/form-input";
import { MaskedFormInput } from "@/core/components/masked-form-input";
import { MoneyInput } from "@/core/components/money-input";
import { Button } from "@/core/components/ui/button";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack";
import {
  Tabs,
  TabsContent,
  TabsListWithIndicator,
  TabsTrigger,
} from "@/core/components/ui/tabs";
import { Textarea } from "@/core/components/ui/textarea";
import { SIDEBAR_MODES, SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { useBusiness } from "@/core/business";
import { numberOnlyMask } from "@/core/masks";
import { getAddressString } from "@/core/utils/address";
import { formatDateTimeWithZone } from "@/core/utils/date";
import { parseMaskedDollarInput } from "@/core/utils/money";
import { CUSTOMER_STATUS } from "@/customer/constants";
import { getCustomerNameAndPhone } from "@/customer/utils";
import { ReferralActivityList } from "@/referrals/common/referral-activity-list";
import { SidebarHeader } from "@/referrals/common/sidebar-header";
import { IReferral, ReferralUpdateParams } from "@/referrals/referral";

interface TableSidebarProps {
  referral: IReferral | undefined;
  onClose: () => void;
  onUpdate: (values: Partial<ReferralUpdateParams>) => void;
  onLost?: () => void;
  onPayout: () => void;
  onDelete: () => void;
}

const serviceTitanJobIdMaskOptions = numberOnlyMask();

export const TableSidebar = ({
  referral,
  onClose,
  onUpdate,
  onLost,
  onPayout,
  onDelete,
}: TableSidebarProps) => {
  const navigate = useNavigate();
  const { businessId, selectedBusiness } = useBusiness();
  const { updateSidebar } = useBusinessSettingsSidebar();

  const [activeTab, setActiveTab] = useState("details");

  const isServiceTitanEnabled =
    selectedBusiness?.service_titan_enabled || false;

  const initalValues = useMemo(
    () => ({
      service_needed: referral?.service_needed || "",
      service_titan_job_id: referral?.service_titan_job_id?.toString() || "",
      deal_size: referral?.deal_size ? referral.deal_size.toString() : "",
      additional_info: referral?.additional_info || "",
    }),
    [referral],
  );

  return (
    <>
      {referral && (
        <Formik
          initialValues={initalValues}
          onSubmit={(values, { resetForm }) => {
            const parsedDealSize = parseMaskedDollarInput(values.deal_size);
            onUpdate({
              ...values,
              service_titan_job_id: values.service_titan_job_id
                ? parseInt(values.service_titan_job_id)
                : null,
              deal_size:
                parsedDealSize !== null && parsedDealSize > 0
                  ? parsedDealSize
                  : null,
            });
            resetForm({ values });
          }}
          enableReinitialize
        >
          {({ values, setFieldValue, getFieldProps, dirty, resetForm }) => (
            <Stack gap="none" fullWidth className="h-full">
              <Form className="flex h-full w-full flex-col">
                <SidebarHeader
                  referral={referral}
                  onClose={onClose}
                  onPayout={onPayout}
                  onLost={onLost}
                  onMoveToPaid={() => onUpdate({ status: "PAID" })}
                  hideIconPayoutButton
                  onDelete={onDelete}
                  actionButton={
                    <Button
                      size="sm"
                      type="button"
                      fullWidth
                      onClick={onPayout}
                    >
                      Pay advocate
                    </Button>
                  }
                />
                <Tabs
                  value={activeTab}
                  onValueChange={setActiveTab}
                  className="flex flex-1 flex-col overflow-hidden pb-6 pl-6"
                >
                  <TabsListWithIndicator className="w-full">
                    <TabsTrigger
                      value="details"
                      className="w-full"
                      onClick={() => setActiveTab("details")}
                    >
                      Details
                    </TabsTrigger>
                    <TabsTrigger
                      value="history"
                      className="w-full"
                      onClick={() => setActiveTab("history")}
                    >
                      History
                    </TabsTrigger>
                  </TabsListWithIndicator>
                  <TabsContent value="details">
                    <ScrollArea className="h-full w-full">
                      <Stack gap="lg" className="py-6 pr-6" fullWidth>
                        <FormInput
                          label="Services needed"
                          placeholder="Services needed"
                          {...getFieldProps("service_needed")}
                          fullWidth
                        />
                        <MoneyInput
                          name="deal_size"
                          label="Deal size"
                          value={values.deal_size}
                          onChange={(value) =>
                            setFieldValue("deal_size", value)
                          }
                          fullWidth
                        />
                        {isServiceTitanEnabled && (
                          <MaskedFormInput
                            name="service_titan_job_id"
                            label="Service Titan Job ID"
                            placeholder="Service Titan Job ID"
                            value={values.service_titan_job_id}
                            onChange={(value) =>
                              setFieldValue("service_titan_job_id", value)
                            }
                            maskOptions={serviceTitanJobIdMaskOptions}
                            fullWidth
                          />
                        )}
                        <Separator />
                        <Textarea
                          placeholder="Notes"
                          label="Notes"
                          className="ml-px"
                          {...getFieldProps("additional_info")}
                        />
                        <Stack direction="row" gap="sm">
                          <Button
                            type="submit"
                            variant="secondary"
                            size="sm"
                            disabled={!dirty}
                          >
                            Save changes
                          </Button>
                          <Button
                            variant="text"
                            size="sm"
                            type="button"
                            disabled={!dirty}
                            onClick={() => resetForm({ values: initalValues })}
                          >
                            Cancel
                          </Button>
                        </Stack>
                        <Separator decorative orientation="horizontal" />
                        <DetailField
                          label="Home address"
                          message={
                            getAddressString(referral.address) || "No address"
                          }
                        />
                        <DetailField
                          label="Advocate"
                          message={getCustomerNameAndPhone(referral.advocate)}
                          extraInfo="The customer who sent the referral (the one you pay out)"
                          onClick={() => {
                            navigate(
                              `/business-settings/${businessId}/customers/${referral?.advocate?.status === CUSTOMER_STATUS.ACTIVE ? "advocates" : "leads"}`,
                            );
                            updateSidebar({
                              section: SIDEBAR_SECTIONS.advocates,
                              mode: SIDEBAR_MODES.view,
                              selectedItemId: referral?.advocate?.id,
                              isOpen: true,
                            });
                          }}
                        />
                        <DetailField
                          label="Referral SMS"
                          message={referral.text_message?.body || "No SMS"}
                          subMessage={
                            referral.created_at
                              ? formatDateTimeWithZone(referral.created_at)
                              : "No from"
                          }
                          extraInfo={`Original referral message sent to ${referral.business?.name} hotline by advocate`}
                        />
                      </Stack>
                    </ScrollArea>
                  </TabsContent>
                  <TabsContent value="history">
                    <ScrollArea className="h-full">
                      <ReferralActivityList referral={referral} />
                    </ScrollArea>
                  </TabsContent>
                </Tabs>
              </Form>
            </Stack>
          )}
        </Formik>
      )}
    </>
  );
};
